
import React, { useState } from 'react';
import { UseFormReturn } from "react-hook-form";
import { TerraceFormValues } from "../SubmissionFormSchema";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, CheckCircle, Loader2, Download } from "lucide-react";
import { SubmissionService } from '@/lib/services/submissionService';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface PhotoReference {
  photo_reference: string;
  cached_url: string | null;
  width: number;
  height: number;
  attribution: string;
  is_cached: boolean;
  photo_source: string;
  source_priority: number;
  is_main_photo?: boolean;
  cached_photo_id?: string;
}

interface PhotoSelectorProps {
  form: UseFormReturn<TerraceFormValues>;
  photos: PhotoReference[];
  setPhotos: React.Dispatch<React.SetStateAction<PhotoReference[]>>;
  submissionId?: string;
  placeName?: string;
  isLoading: boolean;
}

const PhotoSelector: React.FC<PhotoSelectorProps> = ({ 
  form, 
  photos, 
  setPhotos, 
  submissionId, 
  placeName,
  isLoading 
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const photoUrl = form.watch('photo_url');

  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case 'business_owner':
        return 'bg-teal-blue text-white';
      case 'photographer':
        return 'bg-sunset-yellow text-sunset-dark';
      case 'user':
        return 'bg-dusty-rose text-white';
      case 'google_places':
      default:
        return 'bg-sunset-medium text-warm-cream';
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'business_owner':
        return 'Owner';
      case 'photographer':
        return 'Pro';
      case 'user':
        return 'User';
      case 'google_places':
      default:
        return 'Google';
    }
  };

  const handlePhotoSelect = async (photo: PhotoReference) => {
    if (!submissionId) return;
    
    setIsUpdating(true);
    try {
      let photoUrl = photo.cached_url;
      let cachedPhotoId = photo.cached_photo_id;
      
      // Cache photo if not already cached
      if (!photoUrl) {
        const cacheResult = await SubmissionService.cachePhoto(photo.photo_reference);
        if (cacheResult) {
          photoUrl = cacheResult;
          
          // Get the cached photo ID from the database
          const { data: cachedPhoto } = await supabase
            .from('cached_photos')
            .select('id')
            .eq('google_photo_reference', photo.photo_reference)
            .single();
            
          cachedPhotoId = cachedPhoto?.id;
          
          // Update local state
          setPhotos(prevPhotos =>
            prevPhotos.map(p =>
              p.photo_reference === photo.photo_reference
                ? { ...p, cached_url: photoUrl, is_cached: true, cached_photo_id: cachedPhotoId }
                : p
            )
          );
        }
      }

      if (photoUrl) {
        // Update form and submission
        form.setValue('photo_url', photoUrl);
        await SubmissionService.updateSubmissionPhoto(submissionId, photoUrl);
        toast.success('Photo selected and saved');
      } else {
        toast.error('Failed to cache photo');
      }
    } catch (error) {
      console.error('Error selecting photo:', error);
      toast.error('Failed to select photo');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!submissionId) return null;

  return (
    <Card className="card-warm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-medium text-sunset-dark">
              Available Photos from {placeName}
            </div>
            <div className="text-xs text-muted-foreground">
              Photos will be stored in the terrace-photos bucket
            </div>
          </div>
          {photos.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {photos.length} found
            </Badge>
          )}
        </div>
        
        {isLoading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading photos...</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-8">
            <Camera className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              No photos available for this location
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <Card 
                key={photo.photo_reference}
                className={`cursor-pointer transition-all duration-200 ${
                  photoUrl === photo.cached_url
                    ? 'ring-2 ring-peach-orange shadow-sunset' 
                    : 'hover:shadow-lg hover:scale-105'
                }`}
                onClick={() => !isUpdating && handlePhotoSelect(photo)}
              >
                <CardContent className="p-3">
                  <div className="relative">
                    {photo.cached_url ? (
                      <img 
                        src={photo.cached_url} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-24 bg-muted rounded flex items-center justify-center">
                        <div className="text-center">
                          <Download className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                          <div className="text-xs text-muted-foreground">
                            Click to cache
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="absolute top-1 left-1">
                      <Badge 
                        className={`text-xs ${getSourceBadgeColor(photo.photo_source)}`}
                      >
                        {getSourceLabel(photo.photo_source)}
                      </Badge>
                    </div>
                    
                    {photo.is_cached && (
                      <div className="absolute top-1 right-1">
                        <CheckCircle className="h-4 w-4 text-teal-blue bg-white rounded-full" />
                      </div>
                    )}

                    {photoUrl === photo.cached_url && (
                      <div className="absolute inset-0 bg-peach-orange/20 rounded flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-peach-orange" />
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-1 text-xs text-muted-foreground">
                    {photo.width} × {photo.height}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {isUpdating && (
          <div className="text-center py-2">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            <span className="text-sm text-muted-foreground">Processing photo...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PhotoSelector;
